import { MoreHorizontal, Trash2, Eye, Settings, GripVertical, Lock } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FIELD_NO_ACCESS_HINT } from '@/features/records/utils/fieldAccess'
import type { DashboardAnalyticsResponse, DashboardWidget } from '@/types/api/dashboard'
import { ActivityFeedItem } from '@/types/api/dashboard'
import { DashboardWidgetType } from '@/types/enums'

import { DashboardVisibilityDialog } from './DashboardVisibilityDialog'
import { DashboardWidgetSettingsDialog } from './DashboardWidgetSettingsDialog'
import { ActivityFeedWidget } from './widgets/ActivityFeedWidget'
import { BarChartWidget } from './widgets/BarChartWidget'
import { DataTableWidget } from './widgets/DataTableWidget'
import { DonutChartWidget } from './widgets/DonutChartWidget'
import { LineChartWidget } from './widgets/LineChartWidget'
import { NumberCardGroupWidget } from './widgets/NumberCardGroupWidget'
import { NumberCardWidget } from './widgets/NumberCardWidget'
import { PieChartWidget } from './widgets/PieChartWidget'
import { ProgressWidget } from './widgets/ProgressWidget'
import { StackedBarWidget } from './widgets/StackedBarWidget'
import { StatusBadgeGroupWidget } from './widgets/StatusBadgeGroupWidget'

interface DashboardWidgetProps {
  widget: DashboardWidget
  data?: DashboardAnalyticsResponse
  isEditing?: boolean
  lockedRecordGroupId?: string
  onDelete?: (widgetId: string) => void
  onWidgetUpdate?: (widgetId: string, updates: Partial<DashboardWidget>) => void
  onLoadMore?: (widgetId: string, dataSource: string, config: Record<string, unknown>) => Promise<void>
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
}


export function DashboardWidget({
  widget,
  data,
  isEditing = false,
  lockedRecordGroupId,
  onDelete,
  onWidgetUpdate,
  onLoadMore,
  dragHandleProps,
}: DashboardWidgetProps) {
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)

  const isSummaryCard = ([
    DashboardWidgetType.NUMBER_CARD,
    DashboardWidgetType.NUMBER_CARD_GROUP,
    DashboardWidgetType.PROGRESS_BAR,
    DashboardWidgetType.STATUS_BADGE_GROUP,
  ] as string[]).includes(widget.type)

  const renderWidget = () => {
    if (data?.restricted) {
      return (
        <div className="flex h-full min-h-[80px] flex-col items-center justify-center gap-1.5 p-4 text-center">
          <Lock className="h-4 w-4 text-neutral-400" aria-hidden="true" />
          {widget.title && <p className="text-xs font-medium text-neutral-600">{widget.title}</p>}
          <p className="text-xs text-neutral-500">{FIELD_NO_ACCESS_HINT}</p>
        </div>
      )
    }
    switch (widget.type) {
      case DashboardWidgetType.NUMBER_CARD:
        return <NumberCardWidget title={widget.title} metrics={data?.metrics} />
      case DashboardWidgetType.NUMBER_CARD_GROUP:
        return <NumberCardGroupWidget title={widget.title} metrics={data?.metrics} />
      case DashboardWidgetType.BAR_CHART:
        return <BarChartWidget title={widget.title} data={data?.series} />
      case DashboardWidgetType.LINE_CHART:
        return <LineChartWidget title={widget.title} data={data?.series} />
      case DashboardWidgetType.PIE_CHART:
        return <PieChartWidget title={widget.title} data={data?.series} showValuesOnHover />
      case DashboardWidgetType.DONUT_CHART:
        return <DonutChartWidget title={widget.title} data={data?.series} showValuesOnHover />
      case DashboardWidgetType.STACKED_BAR:
        return <StackedBarWidget title={widget.title} data={data?.series} />
      case DashboardWidgetType.TABLE: {
        const rows = Array.isArray((data as Record<string, unknown>)?.items)
          ? ((data as Record<string, unknown>).items as Array<Record<string, unknown>>)
          : []
        return (
          <DataTableWidget
            title={widget.title}
            rows={rows}
            hasMore={data?.hasMore}
            widgetId={widget.id}
            dataSource={widget.dataSource ?? ''}
            dataSourceConfig={widget.dataSourceConfig as Record<string, unknown> ?? {}}
            onLoadMore={onLoadMore}
          />
        )
      }
      case DashboardWidgetType.ACTIVITY_FEED:
        return (
          <ActivityFeedWidget
            title={widget.title}
            items={(data?.items as ActivityFeedItem[]) ?? []}
            hasMore={data?.hasMore}
            widgetId={widget.id}
            dataSource={widget.dataSource ?? ''}
            dataSourceConfig={widget.dataSourceConfig as Record<string, unknown> ?? {}}
            onLoadMore={onLoadMore}
          />
        )
      case DashboardWidgetType.PROGRESS_BAR:
        return <ProgressWidget title={widget.title} usage={data?.usage} />
      case DashboardWidgetType.STATUS_BADGE_GROUP:
        return <StatusBadgeGroupWidget title={widget.title} data={data?.series} />
      default:
        return <NumberCardWidget title={widget.title} metrics={data?.metrics} />
    }
  }

  return (
    <div className="relative flex h-full flex-col">
      <div
        className={[
          'flex-1 min-h-0 overflow-x-hidden rounded-xl bg-white',
          isSummaryCard ? 'overflow-hidden' : 'overflow-y-auto scrollbar-thin',
          isEditing ? 'select-none' : '',
        ].join(' ')}
        style={isSummaryCard ? { minHeight: 80 } : undefined}
      >
        {renderWidget()}
      </div>

      {isEditing && (
        <div
          className="absolute right-2 top-2 z-20 flex items-center gap-1"
          style={{ pointerEvents: 'auto' }}
        >
          <div
            {...dragHandleProps}
            className="flex h-8 w-8 cursor-move items-center justify-center rounded-md bg-white shadow-sm ring-1 ring-black/5 hover:bg-neutral-100 touch-none"
            title="Drag to reorder"
          >
            <GripVertical className="h-4 w-4 text-neutral-500" />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 bg-white shadow-sm ring-1 ring-black/5">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSettingsDialogOpen(true)}>
                <Settings className="mr-2 h-4 w-4" />
                Widget Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setVisibilityDialogOpen(true)}>
                <Eye className="mr-2 h-4 w-4" />
                Visibility
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDelete?.(widget.id)} className="text-red-600">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <DashboardVisibilityDialog
        open={visibilityDialogOpen}
        widgetId={widget.id}
        dashboardId={widget.dashboardId ?? ''}
        initialRoleIds={widget.visibilityRoleIds}
        initialUserIds={widget.visibilityUserIds}
        onOpenChange={setVisibilityDialogOpen}
      />

      <DashboardWidgetSettingsDialog
        open={settingsDialogOpen}
        widget={widget}
        lockedRecordGroupId={lockedRecordGroupId}
        onOpenChange={setSettingsDialogOpen}
        onSave={(updates) => onWidgetUpdate?.(widget.id, updates)}
      />
    </div>
  )
}
